import { AppError, safeParseProject, type PhotobookProject } from '@fotoboek/domain';
import { getDb } from './db';
import { assetBlobStore } from './blobStore';
import type { ProjectMetaRecord } from './types';

function metaFromProject(
  project: PhotobookProject,
  existing?: ProjectMetaRecord,
): ProjectMetaRecord {
  return {
    id: project.id,
    title: project.title,
    productProfileId: project.productProfileId,
    ownerMode: project.ownerMode,
    pageCount: project.pages.length,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    revision: project.revision,
    thumbnailAssetId: existing?.thumbnailAssetId,
    cloudProjectId: project.cloudProjectId ?? existing?.cloudProjectId,
    cloudRevision: existing?.cloudRevision,
    syncState: existing?.syncState ?? (project.ownerMode === 'cloud' ? 'pending' : 'local-only'),
    syncError: existing?.syncError,
  };
}

/** Reads only the metadata record without touching the project document. */
export async function updateProjectMetaFields(
  projectId: string,
  patch: Partial<
    Pick<ProjectMetaRecord, 'cloudProjectId' | 'cloudRevision' | 'syncState' | 'syncError'>
  >,
): Promise<void> {
  const db = getDb();
  const existing = await db.projectsMeta.get(projectId);
  if (!existing) return;
  await db.projectsMeta.put({ ...existing, ...patch });
}

/**
 * Persist a project transactionally with a crash-recovery journal:
 * 1. Write the *draft* journal entry first (small, fast).
 * 2. Commit the real project document + metadata in a single transaction.
 * 3. Clear the journal entry.
 *
 * If the app crashes between (1) and (3), `loadProject` detects the
 * leftover journal entry on next start and recovers from it rather than
 * silently losing the most recent edits.
 */
export async function saveProject(project: PhotobookProject): Promise<void> {
  const db = getDb();
  const json = JSON.stringify(project);

  await db.draftJournal.put({
    projectId: project.id,
    revision: project.revision,
    updatedAt: project.updatedAt,
    json,
  });

  await db.transaction('rw', db.projectDocs, db.projectsMeta, async () => {
    const existing = await db.projectsMeta.get(project.id);
    await db.projectDocs.put({
      projectId: project.id,
      revision: project.revision,
      updatedAt: project.updatedAt,
      json,
    });
    await db.projectsMeta.put(metaFromProject(project, existing));
  });

  const journalEntry = await db.draftJournal.get(project.id);
  if (journalEntry && journalEntry.revision === project.revision) {
    await db.draftJournal.delete(project.id);
  }
}

export async function createProjectRecord(project: PhotobookProject): Promise<void> {
  await saveProject(project);
}

export type LoadResult =
  | { status: 'ok'; project: PhotobookProject; recoveredFromCrash: boolean }
  | { status: 'not-found' }
  | { status: 'corrupt'; message: string };

export async function loadProject(projectId: string): Promise<LoadResult> {
  const db = getDb();
  const [doc, journalEntry] = await Promise.all([
    db.projectDocs.get(projectId),
    db.draftJournal.get(projectId),
  ]);

  let recoveredFromCrash = false;
  let sourceJson: string | undefined = doc?.json;

  if (journalEntry && (!doc || journalEntry.revision > doc.revision)) {
    // The previous save was interrupted after the journal write but before
    // (or during) the final commit. The journal has the newest data.
    sourceJson = journalEntry.json;
    recoveredFromCrash = true;
  }

  if (!sourceJson) {
    return { status: 'not-found' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(sourceJson);
  } catch {
    return { status: 'corrupt', message: 'Projectbestand is beschadigd (ongeldige JSON).' };
  }

  const result = safeParseProject(parsed);
  if (!result.success) {
    return { status: 'corrupt', message: result.error };
  }

  if (recoveredFromCrash) {
    await saveProject(result.data);
  }

  return { status: 'ok', project: result.data, recoveredFromCrash };
}

export async function listProjectMetas(): Promise<ProjectMetaRecord[]> {
  const db = getDb();
  const all = await db.projectsMeta.toArray();
  return all.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export async function getProjectMeta(projectId: string): Promise<ProjectMetaRecord | undefined> {
  return getDb().projectsMeta.get(projectId);
}

/** Finds the local project already linked to a given server project, if any. */
export async function findLocalProjectIdByCloudProjectId(
  cloudProjectId: string,
): Promise<string | undefined> {
  const db = getDb();
  const match = await db.projectsMeta.filter((m) => m.cloudProjectId === cloudProjectId).first();
  return match?.id;
}

export async function deleteProjectCascade(projectId: string): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.projectsMeta, db.projectDocs, db.draftJournal, async () => {
    await db.projectsMeta.delete(projectId);
    await db.projectDocs.delete(projectId);
    await db.draftJournal.delete(projectId);
  });
  await assetBlobStore.removeAllForProject(projectId);
}

export async function saveThumbnail(projectId: string, blob: Blob): Promise<void> {
  const db = getDb();
  await assetBlobStore.put(projectId, 'thumbnail', 'thumbnail', blob);
  const meta = await db.projectsMeta.get(projectId);
  if (meta) {
    await db.projectsMeta.put({ ...meta, thumbnailAssetId: 'thumbnail' });
  }
}

export async function getThumbnailBlob(projectId: string): Promise<Blob | null> {
  return assetBlobStore.get(projectId, 'thumbnail', 'thumbnail');
}

export function assertProjectLoaded(result: LoadResult): PhotobookProject {
  if (result.status === 'ok') return result.project;
  if (result.status === 'not-found') {
    throw new AppError({
      code: 'NOT_FOUND',
      userMessage: 'Dit project kan niet worden gevonden op dit apparaat.',
    });
  }
  throw new AppError({
    code: 'CORRUPT_PROJECT',
    userMessage: 'Dit projectbestand kan niet worden gelezen.',
    message: result.message,
  });
}
